import { Link, useLocation, useNavigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { useEffect, useState } from "react";
import api from "@/lib/api";
import {
  HouseSimple,
  ListChecks,
  ChartPieSlice,
  Target,
  SignOut,
  CurrencyCircleDollar,
} from "@phosphor-icons/react";

const NAV = [
  { to: "/dashboard", label: "Dashboard", Icon: HouseSimple, testId: "nav-dashboard" },
  { to: "/transactions", label: "Transactions", Icon: ListChecks, testId: "nav-transactions" },
  { to: "/budgets", label: "Budgets", Icon: ChartPieSlice, testId: "nav-budgets" },
  { to: "/goals", label: "Goals", Icon: Target, testId: "nav-goals" },
];

export default function AppShell({ children }) {
  const { user, currency, changeCurrency, logout } = useAuth();
  const location = useLocation();
  const navigate = useNavigate();
  const [currencies, setCurrencies] = useState(["USD", "EUR", "GBP", "INR", "CAD", "AUD", "JPY"]);
  const [ccyOpen, setCcyOpen] = useState(false);

  useEffect(() => {
    api.get("/currencies").then(({ data }) => {
      setCurrencies(data.map((c) => c.code));
    }).catch(() => {});
  }, []);

  const handleLogout = async () => {
    await logout();
    navigate("/");
  };

  return (
    <div className="min-h-screen relative z-10">
      {/* Header */}
      <header
        className="sticky top-0 z-30 bg-white border-b-2 border-black"
        data-testid="app-header"
      >
        <div className="max-w-7xl mx-auto flex items-center justify-between px-4 sm:px-6 py-4">
          <Link to="/dashboard" className="flex items-center gap-2" data-testid="brand-link">
            <div className="w-9 h-9 bg-violet-300 border-2 border-black rounded-lg flex items-center justify-center brutal-shadow-sm">
              <span className="font-outfit font-black text-lg">C</span>
            </div>
            <span className="font-outfit font-black text-xl tracking-tight">CampusCash</span>
          </Link>

          <nav className="hidden md:flex items-center gap-1">
            {NAV.map(({ to, label, Icon, testId }) => {
              const active = location.pathname === to;
              return (
                <Link
                  key={to}
                  to={to}
                  data-testid={testId}
                  className={`flex items-center gap-2 px-4 py-2 rounded-full font-manrope font-bold text-sm transition-colors ${
                    active
                      ? "bg-violet-300 border-2 border-black brutal-shadow-sm"
                      : "hover:bg-yellow-100 border-2 border-transparent"
                  }`}
                >
                  <Icon size={18} weight="bold" />
                  {label}
                </Link>
              );
            })}
          </nav>

          <div className="flex items-center gap-2">
            {/* Currency selector */}
            <div className="relative">
              <button
                data-testid="currency-selector-btn"
                onClick={() => setCcyOpen((o) => !o)}
                className="brutal-btn bg-yellow-200 hover:bg-yellow-300 px-3 py-1.5 text-sm flex items-center gap-1.5"
              >
                <CurrencyCircleDollar size={16} weight="bold" />
                {currency}
              </button>
              {ccyOpen && (
                <div
                  className="absolute right-0 mt-2 brutal-card p-2 z-40 max-h-72 overflow-y-auto w-32"
                  data-testid="currency-dropdown"
                >
                  {currencies.map((code) => (
                    <button
                      key={code}
                      data-testid={`currency-option-${code}`}
                      onClick={async () => { await changeCurrency(code); setCcyOpen(false); }}
                      className={`w-full text-left px-3 py-1.5 rounded font-manrope font-bold text-sm hover:bg-violet-200 ${
                        currency === code ? "bg-violet-300" : ""
                      }`}
                    >
                      {code}
                    </button>
                  ))}
                </div>
              )}
            </div>

            <button
              onClick={handleLogout}
              data-testid="logout-btn"
              className="brutal-btn bg-red-200 hover:bg-red-300 px-3 py-1.5 text-sm flex items-center gap-1.5"
              title="Logout"
            >
              <SignOut size={16} weight="bold" />
              <span className="hidden sm:inline">Logout</span>
            </button>
          </div>
        </div>

        {/* Mobile nav */}
        <nav className="md:hidden flex overflow-x-auto gap-1 px-4 pb-3">
          {NAV.map(({ to, label, Icon, testId }) => {
            const active = location.pathname === to;
            return (
              <Link
                key={to}
                to={to}
                data-testid={`mobile-${testId}`}
                className={`flex items-center gap-1.5 px-3 py-1.5 rounded-full font-manrope font-bold text-xs whitespace-nowrap ${
                  active ? "bg-violet-300 border-2 border-black" : "bg-white border-2 border-black/20"
                }`}
              >
                <Icon size={14} weight="bold" />
                {label}
              </Link>
            );
          })}
        </nav>
      </header>

      {/* Content */}
      <main className="max-w-7xl mx-auto px-4 sm:px-6 py-8">
        <div className="text-xs font-bold uppercase tracking-[0.2em] text-black/50 mb-2" data-testid="welcome-label">
          Hey {user?.name || "there"} —
        </div>
        {children}
      </main>

      <footer className="max-w-7xl mx-auto px-6 py-8 text-xs font-manrope text-black/40">
        Built for college life. Stay on top of your money.
      </footer>
    </div>
  );
}
